import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Textarea } from '../../components/ui/textarea';
import {
  Play, CheckCircle, Circle, Clock, FileText, Download, ChevronLeft, ChevronRight,
  BookOpen, Award, Loader2, Menu, X, MessageCircle, Send, Volume2, FileIcon, Eye
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseLearningPage = () => {
  const { courseId } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [activeTab, setActiveTab] = useState('content'); // content | qa
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [answerInputs, setAnswerInputs] = useState({});
  const [submittingQ, setSubmittingQ] = useState(false);

  useEffect(() => { fetchCourseData(); }, [courseId]);
  useEffect(() => { if (currentLesson) fetchQuestions(); }, [currentLesson?.id]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const courseRes = await axios.get(`${API_URL}/courses/${courseId}`);
      setCourse(courseRes.data);

      const chaptersRes = await axios.get(`${API_URL}/elearning/courses/${courseId}/chapters`);
      const chaptersWithLessons = await Promise.all(
        chaptersRes.data.map(async (chapter) => {
          const lessonsRes = await axios.get(`${API_URL}/elearning/chapters/${chapter.id}/lessons`);
          return { ...chapter, lessons: lessonsRes.data };
        })
      );
      setChapters(chaptersWithLessons);

      const progressRes = await axios.get(`${API_URL}/elearning/courses/${courseId}/progress`);
      setProgress(progressRes.data);

      if (progressRes.data.last_accessed_lesson_id) {
        const lesson = await axios.get(`${API_URL}/elearning/lessons/${progressRes.data.last_accessed_lesson_id}`);
        setCurrentLesson(lesson.data);
      } else if (chaptersWithLessons.length > 0 && chaptersWithLessons[0].lessons?.length > 0) {
        const firstLesson = await axios.get(`${API_URL}/elearning/lessons/${chaptersWithLessons[0].lessons[0].id}`);
        setCurrentLesson(firstLesson.data);
      }

      try {
        const certRes = await axios.get(`${API_URL}/elearning/courses/${courseId}/certificate`);
        setCertificate(certRes.data);
      } catch (e) {}
    } catch (error) {
      console.error('Error fetching course data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    if (!currentLesson) return;
    try {
      const res = await axios.get(`${API_URL}/elearning/lessons/${currentLesson.id}/questions`);
      setQuestions(res.data);
    } catch (e) { console.error(e); }
  };

  const selectLesson = async (lessonId) => {
    try {
      const res = await axios.get(`${API_URL}/elearning/lessons/${lessonId}`);
      setCurrentLesson(res.data);
      setActiveTab('content');
      setSidebarOpen(false);
    } catch (error) { console.error(error); }
  };

  const markLessonComplete = async () => {
    if (!currentLesson) return;
    try {
      await axios.post(`${API_URL}/elearning/lessons/${currentLesson.id}/progress`, { completed: true });
      const progressRes = await axios.get(`${API_URL}/elearning/courses/${courseId}/progress`);
      setProgress(progressRes.data);
      toast.success(lang === 'de' ? 'Lektion abgeschlossen!' : 'Leçon terminée !');
      goToNextLesson();
    } catch (error) { console.error(error); }
  };

  const updateVideoProgress = async (positionSeconds) => {
    if (!currentLesson) return;
    try {
      await axios.post(`${API_URL}/elearning/lessons/${currentLesson.id}/progress`, {
        completed: false, video_position_seconds: Math.floor(positionSeconds)
      });
    } catch (error) { console.error(error); }
  };

  const goToNextLesson = () => {
    if (!currentLesson || !chapters.length) return;
    let found = false;
    for (const chapter of chapters) {
      for (const lesson of chapter.lessons || []) {
        if (found) { selectLesson(lesson.id); return; }
        if (lesson.id === currentLesson.id) found = true;
      }
    }
  };

  const goToPrevLesson = () => {
    if (!currentLesson || !chapters.length) return;
    let prevLesson = null;
    for (const chapter of chapters) {
      for (const lesson of chapter.lessons || []) {
        if (lesson.id === currentLesson.id && prevLesson) { selectLesson(prevLesson.id); return; }
        prevLesson = lesson;
      }
    }
  };

  const requestCertificate = async () => {
    try {
      const res = await axios.post(`${API_URL}/elearning/courses/${courseId}/certificate`);
      setCertificate(res.data);
      toast.success(lang === 'de' ? 'Zertifikat erstellt!' : 'Certificat obtenu !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const submitQuestion = async () => {
    if (!newQuestion.trim() || !currentLesson) return;
    setSubmittingQ(true);
    try {
      const res = await axios.post(`${API_URL}/elearning/lessons/${currentLesson.id}/ask`, { content: newQuestion });
      setQuestions(prev => [res.data, ...prev]);
      setNewQuestion('');
      toast.success(lang === 'de' ? 'Frage gesendet' : 'Question envoyée');
    } catch (e) { toast.error('Erreur'); }
    finally { setSubmittingQ(false); }
  };

  const submitAnswer = async (questionId) => {
    const content = answerInputs[questionId];
    if (!content?.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/elearning/questions/${questionId}/answer`, { content });
      setQuestions(prev => prev.map(q => q.id === questionId
        ? { ...q, answers: [...(q.answers || []), res.data], answers_count: (q.answers_count || 0) + 1 }
        : q
      ));
      setAnswerInputs(prev => ({ ...prev, [questionId]: '' }));
    } catch (e) { toast.error('Erreur'); }
  };

  const isLessonCompleted = (lessonId) => {
    if (!progress) return false;
    for (const chapter of progress.chapters || []) {
      for (const lesson of chapter.lessons || []) {
        if (lesson.id === lessonId && lesson.completed) return true;
      }
    }
    return false;
  };

  const resolveUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50" data-testid="course-learning-page">
      {/* ===== SIDEBAR ===== */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed lg:relative lg:translate-x-0 z-40 w-80 h-full bg-white border-r flex flex-col transition-transform duration-300`}>
        <div className="p-4 border-b bg-gradient-to-r from-primary to-primary/80">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-3 text-sm">
            <ChevronLeft className="w-4 h-4" />
            {t('action_back', lang)}
          </button>
          <h2 className="font-heading font-bold text-white text-lg line-clamp-2">{course?.name}</h2>
          <div className="mt-3">
            <div className="flex justify-between text-white/80 text-xs mb-1">
              <span>{progress?.completed_lessons || 0}/{progress?.total_lessons || 0} {lang === 'de' ? 'Lektionen' : 'leçons'}</span>
              <span>{progress?.progress_percentage || 0}%</span>
            </div>
            <Progress value={progress?.progress_percentage || 0} className="h-2 bg-white/20" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {chapters.map((chapter, ci) => (
            <div key={chapter.id} className="border-b">
              <div className="p-4 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-secondary text-white text-xs flex items-center justify-center font-bold">{ci + 1}</span>
                  <h3 className="font-medium text-gray-900 text-sm flex-1">{chapter.title}</h3>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-8">{chapter.lessons_count} {lang === 'de' ? 'Lektionen' : 'leçons'} &bull; {chapter.total_duration_minutes} min</p>
              </div>
              <div className="px-2 pb-2">
                {chapter.lessons?.map((lesson, li) => {
                  const completed = isLessonCompleted(lesson.id);
                  const isCurrent = currentLesson?.id === lesson.id;
                  return (
                    <button key={lesson.id} onClick={() => selectLesson(lesson.id)}
                      className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${isCurrent ? 'bg-primary/10 border border-primary/20' : 'hover:bg-gray-50'}`}
                      data-testid={`lesson-item-${lesson.id}`}
                    >
                      <div className="mt-0.5">
                        {completed ? <CheckCircle className="w-5 h-5 text-green-500" /> : isCurrent ? <Play className="w-5 h-5 text-primary" /> : <Circle className="w-5 h-5 text-gray-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${isCurrent ? 'font-medium text-primary' : 'text-gray-700'}`}>{li + 1}. {lesson.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {lesson.video_type === 'upload' && lesson.video_url?.includes('.mp3') && <Volume2 className="w-3 h-3 text-gray-400" />}
                          {lesson.video_type === 'upload' && lesson.video_url?.includes('.mp4') && <Play className="w-3 h-3 text-gray-400" />}
                          <span className="text-xs text-gray-400">{lesson.video_duration_minutes || 0} min</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {progress?.progress_percentage >= 100 && (
          <div className="p-4 border-t bg-gradient-to-r from-accent/10 to-accent/5">
            {certificate ? (
              <a href={resolveUrl(certificate.pdf_url)} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent/90 transition-colors">
                <Award className="w-5 h-5" />
                {lang === 'de' ? 'Zertifikat herunterladen' : 'Télécharger le certificat'}
              </a>
            ) : (
              <Button onClick={requestCertificate} className="w-full bg-accent hover:bg-accent/90">
                <Award className="w-5 h-5 mr-2" />
                {lang === 'de' ? 'Zertifikat anfordern' : 'Obtenir mon certificat'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Mobile Sidebar Toggle */}
      <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center">
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />}

      {/* ===== MAIN CONTENT ===== */}
      <div className="flex-1 overflow-y-auto">
        {currentLesson ? (
          <div className="max-w-5xl mx-auto p-4 lg:p-8">
            {/* Media Player */}
            <MediaSection lesson={currentLesson} onProgress={updateVideoProgress} onComplete={markLessonComplete} resolveUrl={resolveUrl} />

            {/* Lesson Header */}
            <div className="mb-4">
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{currentLesson.title}</h1>
              {currentLesson.description && <p className="text-gray-600">{currentLesson.description}</p>}
            </div>

            {/* Tabs: Content | Q&A */}
            <div className="flex gap-1 mb-6 border-b">
              <button onClick={() => setActiveTab('content')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'content' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <BookOpen className="w-4 h-4 inline mr-2" />
                {lang === 'de' ? 'Inhalt' : 'Contenu'}
              </button>
              <button onClick={() => setActiveTab('qa')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'qa' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Q&A ({questions.length})
              </button>
            </div>

            {activeTab === 'content' ? (
              <>
                {/* Text Content */}
                {currentLesson.content && (
                  <Card className="mb-6 border-0 shadow-sm">
                    <CardContent className="p-6 prose prose-sm max-w-none">
                      <div dangerouslySetInnerHTML={{ __html: currentLesson.content }} />
                    </CardContent>
                  </Card>
                )}

                {/* Resources */}
                {currentLesson.resources?.length > 0 && (
                  <Card className="mb-6 border-0 shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        {t('sidebar_resources', lang)}
                      </h3>
                      <div className="space-y-3">
                        {currentLesson.resources.map((resource) => {
                          const url = resolveUrl(resource.url);
                          const isPdf = resource.type === 'pdf' || url?.endsWith('.pdf');
                          return (
                            <div key={resource.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                              <FileIcon className="w-5 h-5 text-gray-400 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm">{resource.title}</p>
                                <p className="text-xs text-gray-500">{resource.type.toUpperCase()}</p>
                              </div>
                              <div className="flex gap-2 shrink-0">
                                {isPdf && (
                                  <Button variant="outline" size="sm" asChild>
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                      <Eye className="w-4 h-4 mr-1" /> {lang === 'de' ? 'Ansehen' : 'Voir'}
                                    </a>
                                  </Button>
                                )}
                                <Button variant="outline" size="sm" asChild>
                                  <a href={url} download target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4 mr-1" /> {lang === 'de' ? 'Laden' : 'Télécharger'}
                                  </a>
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              /* Q&A Tab */
              <div className="space-y-6">
                {/* Ask Question */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="font-heading font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-primary" />
                      {lang === 'de' ? 'Eine Frage stellen' : 'Poser une question'}
                    </h3>
                    <div className="flex gap-3">
                      <Textarea
                        value={newQuestion}
                        onChange={(e) => setNewQuestion(e.target.value)}
                        placeholder={lang === 'de' ? 'Ihre Frage zu dieser Lektion...' : 'Votre question sur cette leçon...'}
                        rows={3}
                        className="flex-1"
                        data-testid="qa-question-input"
                      />
                    </div>
                    <Button onClick={submitQuestion} disabled={!newQuestion.trim() || submittingQ} className="mt-3" data-testid="qa-submit-btn">
                      {submittingQ ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {lang === 'de' ? 'Senden' : 'Envoyer'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Questions List */}
                {questions.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{lang === 'de' ? 'Noch keine Fragen. Stellen Sie die erste!' : 'Pas encore de questions. Posez la première !'}</p>
                  </div>
                ) : (
                  questions.map((q) => (
                    <Card key={q.id} className="border-0 shadow-sm">
                      <CardContent className="p-6">
                        <div className="flex gap-3">
                          <Avatar className="w-8 h-8 shrink-0">
                            <AvatarFallback className={q.author_role === 'teacher' || q.author_role === 'admin' ? 'bg-primary text-white' : 'bg-secondary text-white'}>
                              {q.author_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{q.author_name}</span>
                              {(q.author_role === 'teacher' || q.author_role === 'admin') && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{q.author_role === 'teacher' ? (lang === 'de' ? 'Lehrkraft' : 'Enseignant') : 'Admin'}</span>
                              )}
                              <span className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR')}</span>
                            </div>
                            <p className="text-gray-700 mt-1">{q.content}</p>

                            {/* Answers */}
                            {q.answers?.length > 0 && (
                              <div className="mt-4 space-y-3 pl-4 border-l-2 border-gray-100">
                                {q.answers.map(a => (
                                  <div key={a.id} className="flex gap-3">
                                    <Avatar className="w-7 h-7 shrink-0">
                                      <AvatarFallback className={a.author_role === 'teacher' || a.author_role === 'admin' ? 'bg-accent text-white text-xs' : 'bg-gray-200 text-gray-700 text-xs'}>
                                        {a.author_name?.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{a.author_name}</span>
                                        {(a.author_role === 'teacher' || a.author_role === 'admin') && (
                                          <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">{a.author_role === 'teacher' ? (lang === 'de' ? 'Lehrkraft' : 'Enseignant') : 'Admin'}</span>
                                        )}
                                      </div>
                                      <p className="text-gray-600 text-sm mt-0.5">{a.content}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Answer Input */}
                            <div className="mt-3 flex gap-2">
                              <Input
                                value={answerInputs[q.id] || ''}
                                onChange={(e) => setAnswerInputs(prev => ({ ...prev, [q.id]: e.target.value }))}
                                placeholder={lang === 'de' ? 'Antworten...' : 'Répondre...'}
                                className="flex-1 h-9 text-sm"
                                onKeyDown={(e) => e.key === 'Enter' && submitAnswer(q.id)}
                              />
                              <Button size="sm" variant="outline" onClick={() => submitAnswer(q.id)} disabled={!answerInputs[q.id]?.trim()}>
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t">
              <Button variant="outline" onClick={goToPrevLesson} data-testid="prev-lesson-btn">
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('action_previous', lang)}
              </Button>
              {!isLessonCompleted(currentLesson.id) ? (
                <Button onClick={markLessonComplete} className="bg-green-500 hover:bg-green-600" data-testid="mark-complete-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {lang === 'de' ? 'Als erledigt markieren' : 'Marquer comme terminé'}
                </Button>
              ) : (
                <span className="flex items-center gap-2 text-green-500 font-medium">
                  <CheckCircle className="w-5 h-5" />
                  {lang === 'de' ? 'Abgeschlossen' : 'Complété'}
                </span>
              )}
              <Button onClick={goToNextLesson} data-testid="next-lesson-btn">
                {t('action_next', lang)}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{lang === 'de' ? 'Wählen Sie eine Lektion' : 'Sélectionnez une leçon pour commencer'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ===== MEDIA SECTION COMPONENT ===== */
const MediaSection = ({ lesson, onProgress, onComplete, resolveUrl }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  if (!lesson.video_url || lesson.video_type === 'none') return null;

  const url = resolveUrl(lesson.video_url);
  const isUpload = lesson.video_type === 'upload';
  const isAudio = isUpload && (url?.includes('.mp3') || url?.includes('.wav') || url?.includes('.ogg'));
  const isPdf = isUpload && url?.includes('.pdf');
  const isVideo = isUpload && !isAudio && !isPdf;
  const isYoutube = lesson.video_type === 'youtube';
  const isVimeo = lesson.video_type === 'vimeo';

  // YouTube embed
  if (isYoutube) {
    const videoId = url?.match(/(?:v=|\/)([\w-]{11})/)?.[1];
    return (
      <div className="mb-6 aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
        <iframe src={`https://www.youtube.com/embed/${videoId}`} title={lesson.title}
          className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }

  // Vimeo embed
  if (isVimeo) {
    const vimeoId = url?.match(/vimeo\.com\/(\d+)/)?.[1];
    return (
      <div className="mb-6 aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
        <iframe src={`https://player.vimeo.com/video/${vimeoId}`} title={lesson.title}
          className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }

  // PDF Viewer
  if (isPdf) {
    return (
      <div className="mb-6 rounded-xl overflow-hidden bg-white shadow-lg border">
        <div className="bg-gray-100 px-4 py-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Document PDF
          </span>
          <Button variant="outline" size="sm" asChild>
            <a href={url} download target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-1" /> Télécharger
            </a>
          </Button>
        </div>
        <iframe src={url} title={lesson.title} className="w-full" style={{ height: '70vh' }} />
      </div>
    );
  }

  // Audio Player
  if (isAudio) {
    return (
      <div className="mb-6 rounded-xl overflow-hidden bg-gradient-to-r from-primary/5 to-secondary/5 shadow-sm border p-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Volume2 className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{lesson.title}</h3>
            <p className="text-sm text-gray-500">Audio &bull; {lesson.video_duration_minutes || 0} min</p>
          </div>
        </div>
        <audio ref={audioRef} controls className="w-full" src={url}
          onTimeUpdate={() => { if (audioRef.current) onProgress(audioRef.current.currentTime); }}
          onEnded={onComplete}
        />
      </div>
    );
  }

  // Video Player (uploaded MP4)
  if (isVideo) {
    return (
      <div className="mb-6 aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
        <video ref={videoRef} controls className="w-full h-full" src={url}
          onTimeUpdate={() => { if (videoRef.current) onProgress(videoRef.current.currentTime); }}
          onEnded={onComplete}
        />
      </div>
    );
  }

  return null;
};

export default CourseLearningPage;
